import * as React from 'react';
import { Box, styled, Typography, TableHead } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { getOrders, getOrdersTableData } from '../../services/cloudFirestore';
import { UserContext } from '../../providers/UserProvider';
import { SalesTableViewData } from '../../models/SalesTableViewData';
import { TablePaginationActions } from './TablePaginationActions';
import { StyledButtonTable, StyledDashboardHeading } from '../../theme/StyledElements';
import { ModalContainer } from '../ModalContainer';
import { ClientDetailsHeader } from '../Dashboard/ClientDetailsHeader';
import { useMountedRef } from '../../hooks/useMountedRef';
import { getSalesTableData } from '../../utils';

const StyledTableCell = styled(TableCell)(() => ({
    width: 160,
    color: '#FFFFFF'
}));

const StyledTableTypography = styled(Typography)(() => ({
    textTransform: 'none',
    letterSpacing: 0,
    fontWeight: 500
}));

export function SalesTable(): React.ReactElement {
    const { t } = useTranslation();
    const mounted = useMountedRef();
    const user = useContext(UserContext);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [orderData, setOrderData] = useState<SalesTableViewData[]>([]);
    const [specificOrderData, setSpecificOrderData] = useState<SalesTableViewData>({
        id: undefined,
        orderNumber: undefined,
        date: undefined,
        clients: undefined,
        noOfBaskets: undefined,
        cost: undefined,
        status: undefined
    });
    const [openDetailsModal, setOpenDetailsModal] = useState<boolean>(false);

    useEffect(() => {
        if (user && user.idProducteur && mounted.current) {
            getOrders(user.idProducteur).then((data) => {
                const updatedList = getSalesTableData(data);
                getOrdersTableData(updatedList).then((viewData) => {
                    setOrderData(viewData);
                });
            });
        }
    }, []);

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - orderData.length) : 0;

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ): void => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <ModalContainer
                isModelOpen={openDetailsModal}
                setModelOpen={setOpenDetailsModal}
                title={<ClientDetailsHeader salesData={specificOrderData} />}
                onButtonClick={(): void => {
                    setOpenDetailsModal(false);
                }}
                id={specificOrderData.id}
            />
            {orderData.length > 0 && (
                <Box>
                    <StyledDashboardHeading variant="h3" sx={{ margin: '40px 0px 20px 0px' }}>
                        {t('myLastSales')}
                    </StyledDashboardHeading>
                    <TableContainer component={Paper} sx={{ width: '880px' }}>
                        <Table
                            sx={{ minWidth: 500, color: '#134C03' }}
                            aria-label="custom pagination table">
                            <TableHead sx={{ backgroundColor: '#134C03' }}>
                                <TableRow>
                                    <StyledTableCell>{t('number')}</StyledTableCell>
                                    <StyledTableCell>{t('date')}</StyledTableCell>
                                    <StyledTableCell>{t('client(s)')}</StyledTableCell>
                                    <StyledTableCell>{t('noOfBaskets')}</StyledTableCell>
                                    <StyledTableCell>{t('ca')}</StyledTableCell>
                                    <StyledTableCell>{t('status')}</StyledTableCell>
                                    <StyledTableCell>{t('details')}</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(rowsPerPage > 0
                                    ? orderData.slice(
                                          page * rowsPerPage,
                                          page * rowsPerPage + rowsPerPage
                                      )
                                    : orderData
                                ).map((row) => (
                                    <TableRow key={row.orderNumber}>
                                        <TableCell style={{ width: 160 }}>
                                            <StyledTableTypography
                                                variant="overline"
                                                sx={{
                                                    color:
                                                        row.status === 'En cours'
                                                            ? '#E37F1E'
                                                            : '#394A35'
                                                }}>
                                                {row.orderNumber}
                                            </StyledTableTypography>
                                        </TableCell>
                                        <TableCell style={{ width: 160 }}>
                                            <StyledTableTypography
                                                variant="overline"
                                                sx={{
                                                    color:
                                                        row.status === 'En cours'
                                                            ? '#E37F1E'
                                                            : '#394A35'
                                                }}>
                                                {row.date}
                                            </StyledTableTypography>
                                        </TableCell>
                                        <TableCell style={{ width: 160 }}>
                                            <StyledTableTypography
                                                variant="overline"
                                                sx={{
                                                    color:
                                                        row.status === 'En cours'
                                                            ? '#E37F1E'
                                                            : '#394A35'
                                                }}>
                                                {row.clients && row.clients.join(', ')}
                                            </StyledTableTypography>
                                        </TableCell>
                                        <TableCell style={{ width: 160 }}>
                                            <StyledTableTypography
                                                variant="overline"
                                                sx={{
                                                    color:
                                                        row.status === 'En cours'
                                                            ? '#E37F1E'
                                                            : '#394A35'
                                                }}>
                                                {row.noOfBaskets}
                                            </StyledTableTypography>
                                        </TableCell>
                                        <TableCell style={{ width: 160 }}>
                                            <StyledTableTypography
                                                variant="overline"
                                                sx={{
                                                    color:
                                                        row.status === 'En cours'
                                                            ? '#E37F1E'
                                                            : '#394A35'
                                                }}>
                                                {row.cost?.toFixed(2)}€
                                            </StyledTableTypography>
                                        </TableCell>
                                        <TableCell style={{ width: 160 }}>
                                            <StyledTableTypography
                                                variant="overline"
                                                sx={{
                                                    color:
                                                        row.status === 'En cours'
                                                            ? '#E37F1E'
                                                            : '#394A35'
                                                }}>
                                                {row.status}
                                            </StyledTableTypography>
                                        </TableCell>
                                        <TableCell style={{ width: 160 }}>
                                            <StyledButtonTable
                                                type="button"
                                                onClick={(): void => {
                                                    setSpecificOrderData(row);
                                                    setOpenDetailsModal(true);
                                                }}
                                                sx={{
                                                    marginBottom: '18px',
                                                    color:
                                                        row.status === 'En cours'
                                                            ? '#E37F1E'
                                                            : '#394A35'
                                                }}>
                                                <Typography
                                                    variant="overline"
                                                    sx={{
                                                        textTransform: 'none',
                                                        letterSpacing: 0,
                                                        fontWeight: 500,
                                                        color:
                                                            row.status === 'En cours'
                                                                ? '#E37F1E'
                                                                : '#394A35'
                                                    }}>
                                                    {t('details')}
                                                </Typography>
                                            </StyledButtonTable>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {emptyRows > 0 && (
                                    <TableRow style={{ color: '#134C03', height: 53 * emptyRows }}>
                                        <TableCell colSpan={6} />
                                    </TableRow>
                                )}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        sx={{
                                            color: '#134C03',
                                            width: '100%'
                                        }}
                                        rowsPerPageOptions={[
                                            5,
                                            10,
                                            25,
                                            { label: 'All', value: -1 }
                                        ]}
                                        colSpan={7}
                                        count={orderData.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        SelectProps={{
                                            inputProps: {
                                                'aria-label': 'Vue par page',
                                                color: '#134C03'
                                            },
                                            native: true
                                        }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        ActionsComponent={TablePaginationActions}
                                        labelRowsPerPage={t('rowsPerPage')}
                                        labelDisplayedRows={({ from, to, count }): string => {
                                            return `${from}–${to} sur ${
                                                count !== -1 ? count : `more than ${to}`
                                            }`;
                                        }}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </>
    );
}
